import React, { ReactNode, createContext, useContext, useState, useEffect } from 'react';

type LayoutCtx = {
  color: string;
  layout: string;
  src: string;
  height: number;
  positionX: number;
  positionY: number;
  hasBackButton: boolean;
  hasChangeLanguage: boolean;
  setLayoutInfo: (
    newColor: string,
    newLayout: string,
    newSrc: string,
    newHeight: number,
    newPositionX: number,
    newPositionY: number,
    newHasBackButton: boolean,
    hasChangeLanguage: boolean,
  ) => void;
  pageTitle: ReactNode;
  setPageTitle: (title: ReactNode) => void;
};

export const LayoutContext = createContext<LayoutCtx>({
  color: '',
  layout: '',
  src: '',
  height: 0,
  positionX: 0,
  positionY: 0,
  hasBackButton: true,
  hasChangeLanguage: false,
  setLayoutInfo: () => {},
  pageTitle: '',
  setPageTitle: () => {},
});

export const LayoutProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [color, setColor] = useState<string>('');
  const [layout, setLayout] = useState<string>('');
  const [src, setSrc] = useState<string>('');
  const [height, setHeight] = useState<number>(0);
  const [positionX, setPositionX] = useState<number>(0);
  const [positionY, setPositionY] = useState<number>(0);
  const [hasBackButton, setHasBackButton] = useState<boolean>(false);
  const [hasChangeLanguage, setHasChangeLanguage] = useState<boolean>(false);
  const [pageTitle, setPageTitle] = useState<ReactNode>('');

  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', color);
  }, [color]);

  const handleSetLayoutInfo = (
    newColor: string,
    newLayout: string,
    newSrc: string,
    newHeight: number,
    newPositionX: number,
    newPositionY: number,
    newHasBackButton: boolean,
    newHasChangedLanguage: boolean,
  ) => {
    setColor(newColor);
    setLayout(newLayout);
    setSrc(newSrc);
    setHeight(newHeight);
    setPositionX(newPositionX);
    setPositionY(newPositionY);
    setHasBackButton(newHasBackButton);
    setHasChangeLanguage(newHasChangedLanguage);
  };

  return (
    <LayoutContext.Provider
      value={{
        color,
        layout,
        src,
        height,
        positionX,
        positionY,
        hasBackButton,
        hasChangeLanguage,
        setLayoutInfo: handleSetLayoutInfo,
        pageTitle,
        setPageTitle,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutCtx => useContext(LayoutContext);
